import { AlertTitle, Chip, Stack, Typography } from '@montugroup/design-system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockClockIcon from '@mui/icons-material/LockClock';
import { useTheme } from '@mui/material';
import { DispensingTimeline, DispensingTimelineLegend } from './DispensingTimeline';
import type { OkToDispenseOrderProduct } from './unable-to-dispense.types';

const getIntervalWindowText = ({ quantity, duration }: { quantity: number; duration: number }) =>
  `${quantity} units/repeat • ${duration} day interval`;

export function DispenseIntervalAlert({ orderProduct }: { orderProduct: OkToDispenseOrderProduct }) {
  const theme = useTheme();

  const previousIntervalWindowText =
    orderProduct.previousWindow &&
    getIntervalWindowText({
      quantity: orderProduct.previousWindow.maxUnits,
      duration: orderProduct.previousWindow.interval,
    });
  const currentIntervalWindowText = getIntervalWindowText({
    quantity: orderProduct.currentWindow.maxUnits,
    duration: orderProduct.currentWindow.interval,
  });
  const hasChangedIntervalWindow =
    previousIntervalWindowText && previousIntervalWindowText !== currentIntervalWindowText;

  return (
    <Stack
      sx={{
        backgroundColor: theme.palette.success.light,
        borderColor: theme.palette.success.main,
        padding: 4,
        paddingTop: 3,
        paddingBottom: 3,
        borderRadius: 1,
        borderWidth: '1px',
        borderStyle: 'solid',
      }}
    >
      <Stack direction={'row'} flex={1} gap={3}>
        <LockClockIcon color={'success'} />
        <Stack direction={'column'} flex={1} gap={1}>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-start'}>
            <Stack rowGap={1}>
              <AlertTitle sx={{ marginBottom: 0, marginTop: 0, fontWeight: 600, color: theme.palette.success.dark }}>
                {orderProduct.units}x {orderProduct.product.name}
              </AlertTitle>
              {!hasChangedIntervalWindow && (
                <Typography variant="caption" fontWeight={600} color="textSecondary">
                  {currentIntervalWindowText}
                </Typography>
              )}
            </Stack>
            <Chip
              icon={<CheckCircleIcon color="inherit" />}
              label={'OK to dispense'}
              variant="filled"
              sx={{
                color: theme.palette.success.light,
                backgroundColor: theme.palette.success.main,
              }}
            />
          </Stack>

          {/* HACK: This marginBottom={4} is just hardcoded spacing between the DispensingTimeline and the DispensingTimelineLegend to allow space for labels below the timeline */}
          <Stack direction={'row'} justifyContent={'space-between'} gap={3} marginBottom={4}>
            <Stack direction={'column'} flex={1} gap={1}>
              <Typography variant="caption" fontWeight={600}>
                Previous Repeat {hasChangedIntervalWindow && `(${previousIntervalWindowText})`}
              </Typography>

              <DispensingTimeline dispenseWindow={orderProduct.previousWindow} />
            </Stack>
            <Stack direction={'column'} flex={1} gap={1}>
              <Typography variant="caption" fontWeight={600}>
                Current Repeat {hasChangedIntervalWindow && `(${currentIntervalWindowText})`}
              </Typography>

              <DispensingTimeline
                dispenseWindow={orderProduct.currentWindow}
                currentOrderQuantity={orderProduct.units}
              />
            </Stack>
          </Stack>
          <DispensingTimelineLegend />
        </Stack>
      </Stack>
    </Stack>
  );
}
